/**
 * PreviewCasePath — internal preview page (Preview* pattern) mounting the
 * Phase 2 CasePathShell stage-rail skeleton with realistic Summit Ridge
 * fixture data. Mirrors PreviewVerificationLayer.tsx's structure. Fixtures
 * only — no live data, no auth hooks; safe under ProtectedRoute and in the
 * local render harness alike.
 */
import React, { useState } from 'react';
import CasePathShell from '../components/case-path/CasePathShell';
import { NextBestActionCard, CaseStrengthMeter, DefensePreviewCard } from '../components/case-path/RailCards';
import AgentTrail, { DeliberationEvent } from '../components/shared/AgentTrail';
import type { CaseSpineState, Stage } from '../lib/casePath';
import type { ChipState } from '../components/shared/StateChip';
import FlagsInbox, { CaseFlag } from '../components/case-path/FlagsInbox';

const FIXTURE_STATE: CaseSpineState = {
  stage: 'calculate',
  posture: 'claim',
  activePaths: new Set(['time_element', 'property']),
  verificationDebt: { engines_expected: 20, engines_run: 17, blockers: 2, warnings: 1 },
};

const STAGE_CHIPS: Record<Stage, ChipState> = {
  setup: 'verified',
  foundation: 'verified',
  method: 'verified',
  calculate: 'system-active',
  attest: 'dormant',
  deliver: 'dormant',
};

const FIXTURE_EVENTS: DeliberationEvent[] = [
  { agent: 'ai:forensic', round: 1, tool_name: 'get_engine_outputs', inputs: { report_id: 'd6d88aea' }, result_summary: 'Loaded 17 canonical engine outputs', duration_ms: 412, ts: '2026-07-24T18:41:02Z' },
  { agent: 'ai:attorney', round: 1, tool_name: 'query_war_room_intelligence', inputs: { query: 'CP 04 11 endorsement grounding' }, result_summary: 'Endorsement grounded against policy DI; 1 blocker raised', duration_ms: 980, ts: '2026-07-24T18:42:20Z' },
  { agent: 'ai:adjuster', round: 2, tool_name: 'get_policy_deductions', inputs: { report_id: 'd6d88aea' }, result_summary: 'Waiting-period deduction confirmed ($3,090.63)', duration_ms: 640, ts: '2026-07-24T18:42:41Z' },
];

const FIXTURE_FLAGS: CaseFlag[] = [
  {
    id: 'fixture-1',
    severity: 'blocker',
    title: '[FIXTURE] CP 04 11 endorsement grounding: policy basis unresolved',
    detail: '[FIXTURE] Policy basis unresolved — verification required',
    source: { kind: 'engine', ref: 'policy_deductions' },
    raised_at: '2026-07-25T10:00:00Z',
    status: 'open',
  },
  {
    id: 'fixture-2',
    severity: 'blocker',
    title: '[FIXTURE] Mitigation CONTESTED: carrier disputes mitigation adequacy',
    detail: '[FIXTURE] Carrier disputes mitigation adequacy — review needed',
    source: { kind: 'engine', ref: 'mitigation' },
    raised_at: '2026-07-25T10:01:00Z',
    status: 'open',
  },
  {
    id: 'fixture-3',
    severity: 'warning',
    title: '[FIXTURE] enhanced_claim data is stale and may not reflect latest uploads',
    detail: '[FIXTURE] Data may not reflect latest uploads — refresh advised',
    source: { kind: 'document', ref: 'enhanced_claim' },
    raised_at: '2026-07-25T10:02:00Z',
    status: 'open',
  },
];

export default function PreviewCasePath() {
  const [attested, setAttested] = useState(false);
  const [flags, setFlags] = useState<CaseFlag[]>(FIXTURE_FLAGS);

  const handleAcknowledge = (id: string) => {
    setFlags((prev) =>
      prev.map((f) => (f.id === id ? { ...f, status: 'acknowledged' as const } : f))
    );
  };

  const handleDispute = (id: string, reason: string) => {
    setFlags((prev) =>
      prev.map((f) => (f.id === id ? { ...f, status: 'disputed' as const, disposition_reason: reason } : f))
    );
  };

  const handleWaive = (id: string, reason: string) => {
    setFlags((prev) =>
      prev.map((f) => (f.id === id ? { ...f, status: 'waived' as const, disposition_reason: reason } : f))
    );
  };

  const handleSourceClick = (_flag: CaseFlag) => {
    // Fixture: no-op — in production this would open SourceDrawer/PDF
  };

  const section: React.CSSProperties = { marginBottom: 24 };
  const h2: React.CSSProperties = {
    fontSize: 12, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase',
    color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border)', paddingBottom: 8,
  };
  const card: React.CSSProperties = {
    background: 'var(--color-bg-elevated)', border: '1px solid var(--color-hairline)',
    borderRadius: 6, padding: 16, marginTop: 14,
  };

  const railCards = (
    <>
      <FlagsInbox
        flags={flags}
        onAcknowledge={handleAcknowledge}
        onDispute={handleDispute}
        onWaive={handleWaive}
        onSourceClick={handleSourceClick}
      />
      <NextBestActionCard
        action_label="Review CP 04 11 endorsement grounding against policy DI"
        dollar_impact={86340}
        engine_id="policy_deductions"
        onGo={() => {}}
      />
      <CaseStrengthMeter score0to100={72} blockers={2} evidencePct={85} />
      <DefensePreviewCard
        daubert_score_0_100={81}
        top_attacks={[
          { methodology: 'Bottom-up reconstruction', attack: 'Historical baseline uses fewer than 24 months of P&L data' },
          { methodology: 'Policy deductions', attack: 'Waiting-period threshold not independently corroborated by adjuster notes' },
        ]}
      />
    </>
  );

  return (
    <CasePathShell
      businessName="Summit Ridge Grill"
      eventLabel="Fire loss — March 2023 · HARTFORD"
      headlineValue={155309.37}
      headlineProvenance="E"
      propertyRcv={257000}
      enginesRun={17}
      enginesExpected={20}
      blockers={2}
      state={FIXTURE_STATE}
      gateInputs={{}}
      onAttest={() => setAttested(true)}
      stageChips={STAGE_CHIPS}
      railCards={railCards}
      agentTrail={<AgentTrail events={FIXTURE_EVENTS} />}
    >
      <div className="font-sans">
        <h1 className="font-serif" style={{ fontSize: 22, fontWeight: 400, marginTop: 0, color: 'var(--color-text-primary)' }}>
          Case Path — preview (Summit Ridge fixtures)
        </h1>
        <p style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
          Phase 2 stage-rail shell. Header, 6-stage rail, and collapsible right rail are wired
          against fixture data — no live fetches, no auth hooks.
        </p>

        <section style={section}>
          <h2 style={h2}>Attestation state</h2>
          <div style={card}>
            <p style={{ fontSize: 12, color: 'var(--color-text-secondary)', margin: 0 }}>
              {attested
                ? 'Attested — onAttest fired from the header button.'
                : 'Not attested. With 2 blockers outstanding, the Attest button stays disabled until stage reaches attest with zero blockers.'}
            </p>
          </div>
        </section>
      </div>
    </CasePathShell>
  );
}
